import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { HeroSlider } from '../components/HeroSlider';
import { ProductRow } from '../components/product/ProductRow';
import { BundleSection } from '../components/product/BundleSection';
import { BrandsMarquee } from '../components/BrandsMarquee';
import { PromoMarquee } from '../components/PromoMarquee';
import { HomepageCategories } from '../components/HomepageCategories';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function HomePage({ isPreviewMode = false }) {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const lang = i18n.language?.split('-')[0] || 'fr';
  
  // Check if in preview mode from URL param
  const previewToken = searchParams.get('token');
  const inPreview = isPreviewMode || !!previewToken;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch products and categories in parallel
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(`${API_URL}/api/products?limit=100`),
          axios.get(`${API_URL}/api/categories`)
        ]);
        
        setProducts(productsRes.data.products || []);
        setCategories(categoriesRes.data.categories || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Dernières arrivées - Produits les plus récents (basé sur created_at)
  const newArrivals = [...products]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 12);
  
  // Les plus populaires - Produits avec badge trending ou aléatoire parmi les publiés
  const popularProducts = products.filter(p => p.badges?.is_trending).length > 0
    ? products.filter(p => p.badges?.is_trending).slice(0, 12)
    : [...products].sort(() => Math.random() - 0.5).slice(0, 12);
  
  // Catégories par type
  const productCategories = categories.filter(c => c.type === 'product' || !c.type);
  const brandCategories = categories.filter(c => c.type === 'brand');

  return (
    <div className="min-h-screen flex flex-col bg-mutagim-dark" data-testid="home-page">
      <Header isPreviewMode={inPreview} />
      
      <main className="flex-1">
        {/* Promo Marquee - Top */}
        <PromoMarquee />
        
        {/* Hero Slider */}
        <HeroSlider />
        
        {/* Brands Marquee - After Hero */}
        <BrandsMarquee />
        
        {/* Content */}
        <div className="max-w-7xl mx-auto py-8 space-y-12">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-mutagim-red" />
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-red-500">{error}</p>
            </div>
          ) : (
            <>
              {/* 1. DERNIÈRES ARRIVÉES - Tout en haut */}
              {newArrivals.length > 0 && (
                <ProductRow 
                  title={lang === 'fr' ? '🆕 Dernières Arrivées' : lang === 'en' ? '🆕 New Arrivals' : '🆕 חדשים'} 
                  products={newArrivals} 
                />
              )}
              
              {/* 2. LES PLUS POPULAIRES */}
              {popularProducts.length > 0 && (
                <ProductRow 
                  title={lang === 'fr' ? '🔥 Les Plus Populaires' : lang === 'en' ? '🔥 Most Popular' : '🔥 הכי פופולריים'} 
                  products={popularProducts} 
                />
              )}
              
              {/* 3. CATÉGORIES PAR TYPOLOGIE */}
              {productCategories.length > 0 && (
                <section className="px-4 md:px-0" data-testid="product-categories-section">
                  <h2 className="text-xl md:text-2xl font-bold text-white mb-6">
                    {lang === 'fr' ? '📦 Par Type de Produit' : lang === 'en' ? '📦 By Product Type' : '📦 לפי סוג מוצר'}
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {productCategories.slice(0, 8).map((category) => (
                      <a
                        key={category.slug}
                        href={`/category/${category.slug}`}
                        className="relative group overflow-hidden rounded-xl aspect-[3/2] bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-800 hover:border-mutagim-red/50 transition-all"
                        data-testid={`category-${category.slug}`}
                      >
                        {category.image_url ? (
                          <img 
                            src={category.image_url} 
                            alt={category.name?.[lang] || category.name?.fr}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-4xl">{category.emoji || '🏷️'}</span>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                        <div className="absolute bottom-3 start-3 end-3">
                          <h3 className="text-lg font-bold text-white group-hover:text-mutagim-red transition-colors">
                            {category.name?.[lang] || category.name?.fr || category.slug}
                          </h3>
                          <p className="text-xs text-gray-400">{category.products_count || 0} articles</p>
                        </div>
                      </a>
                    ))}
                  </div>
                </section>
              )}
              
              {/* 4. CATÉGORIES PAR MARQUE */}
              {brandCategories.length > 0 && (
                <section className="px-4 md:px-0" data-testid="brand-categories-section">
                  <h2 className="text-xl md:text-2xl font-bold text-white mb-6">
                    {lang === 'fr' ? '🏷️ Par Marque' : lang === 'en' ? '🏷️ By Brand' : '🏷️ לפי מותג'}
                  </h2>
                  <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {brandCategories.slice(0, 12).map((brand) => (
                      <a
                        key={brand.slug}
                        href={`/category/${brand.slug}`}
                        className="relative group overflow-hidden rounded-lg aspect-square bg-zinc-900 border border-zinc-800 hover:border-indigo-500/50 transition-all flex items-center justify-center"
                        data-testid={`brand-${brand.slug}`}
                      >
                        {brand.logo_url ? (
                          <img 
                            src={brand.logo_url} 
                            alt={brand.name?.[lang] || brand.name?.fr || brand.slug}
                            className="w-16 h-16 object-contain filter brightness-0 invert group-hover:brightness-100 group-hover:invert-0 transition-all"
                          />
                        ) : (
                          <span className="text-white font-bold text-sm text-center px-2">
                            {brand.name?.[lang] || brand.name?.fr || brand.slug}
                          </span>
                        )}
                        <div className="absolute bottom-2 left-0 right-0 text-center">
                          <span className="text-[10px] text-gray-500">{brand.products_count || 0}</span>
                        </div>
                      </a>
                    ))}
                  </div>
                </section>
              )}
              
              {/* Packs & Bundles */}
              <BundleSection />
            </>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

export default HomePage;
// Force rebuild Mon Mar 23 01:30:29 UTC 2026
